import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
	Button,
	FormControl,
	FormHelperText,
	IconButton,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	Textarea,
	VStack,
	useDisclosure,
	useToast,
	Box,
	Divider,
} from "@chakra-ui/react";
import { BiErrorCircle } from "react-icons/bi";
import { FiSend } from "react-icons/fi";
import { MdBugReport } from "react-icons/md";


import { API } from "../../utils/http";

export default function ReportButton() {
	const currentUserEmail = useSelector((state) => state.user.user?.email);
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const { isOpen, onOpen, onClose } = useDisclosure();
	const toast = useToast();
	const { t } = useTranslation();

	const submitReport = () => {
		if (!title.trim() || !description.trim()) {
			toast({
				title: "Missing Information",
				description: "Please fill in both title and description",
				status: "warning",
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		const payload = { title, description, email: currentUserEmail };
		API.post("report-bug", payload)
			.then(() => {
				toast({
					title: "Bug Report Submitted",
					description: "Thank you for helping us improve! We'll look into this issue.",
					status: "success",
					duration: 4000,
					isClosable: true,
				});
				handleCloseModal();
			})
			.catch((e) => {
				toast({
					title: "Error Submitting Report",
					description: e.message,
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			});
	};

	const handleCloseModal = () => {
		onClose();
		setTitle("");
		setDescription("");
	};

	return (
		<>
			<Box position="fixed" bottom="20px" right="20px" zIndex={999}>
				<IconButton
					isRound={true}
					variant="solid"
					colorScheme="teal"
					aria-label="Report a bug"
					fontSize="24px"
					icon={<MdBugReport />}
					title="Report a bug"
					onClick={onOpen}
					boxShadow="lg"
					_hover={{
						transform: "scale(1.1)",
						transition: "all 0.2s ease-in-out",
					}}
				/>
			</Box>

			<Modal isOpen={isOpen} onClose={handleCloseModal} size="lg">
				<ModalOverlay />
				<ModalContent>
					<ModalHeader display="flex" alignItems="center" gap={2}>
						<BiErrorCircle size="24px" color="#319795" />
						Raportează o eroare
					</ModalHeader>
					<ModalCloseButton />
					<Divider />
					<ModalBody>
						<VStack spacing={4} align="stretch" py={2}>
							<Text fontSize="sm" color="gray.600">
								Ai găsit ceva care nu funcționează așa cum te așteptai? Ajută-ne să ne îmbunătățim prin trimiterea unui raport de eroare. Te rugăm să furnizezi
								cât mai multe detalii posibil pentru a ne ajuta să înțelegem și să rezolvăm problema.
							</Text>

							<FormControl isRequired>
								<Input
									placeholder="Titlu eroare"
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									size="md"
									_focus={{
										borderColor: "teal.500",
										boxShadow: "0 0 0 1px teal.500",
									}}
								/>
								<FormHelperText>Un titlu scurt și descriptiv pentru eroare (de exemplu, "Nu se pot salva detaliile proprietății")</FormHelperText>
							</FormControl>

							<FormControl isRequired>
								<Textarea
									placeholder="Descriere detaliată"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									size="md"
									minHeight="150px"
									_focus={{
										borderColor: "teal.500",
										boxShadow: "0 0 0 1px teal.500",
									}}
								/>
								<FormHelperText>
									Vă rugăm să includeți: ce făceați, ce a mers prost și ce vă așteptați să se întâmple. Pașii pentru a reproduce problema sunt foarte utili.
								</FormHelperText>
							</FormControl>
						</VStack>
					</ModalBody>

					<ModalFooter gap={3}>
						<Button variant="outline" onClick={handleCloseModal}>
							{t("misc.cancel")}
						</Button>
						<Button colorScheme="teal" onClick={submitReport} leftIcon={<FiSend />} isDisabled={!title.trim() || !description.trim()}>
							{t("misc.send")}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
}
